import React, { useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useBlogPostEditorState } from './useBlogPostEditorState';
import { useBlogPostEditorHandlers } from './useBlogPostEditorHandlers';
import { useIsMimicking } from './useIsMimicking';
import { useUnpublishedSummary } from '../../hooks/useUnpublishedSummary';
import { useBlogPostPreview } from '../../hooks/useBlogPostPreview';
import { BlogPostEditorHeader } from './BlogPostEditorHeader';
import { BlogPostEditorContent } from './BlogPostEditorContent';
import { BlogPostEditorSidebar } from './BlogPostEditorSidebar';
import { ToastContainer } from '../../components/Toast';
import { DeleteConfirmModal } from '../../components/DeleteConfirmModal';
import { SEOAnalysisPanel } from '../../components/SEOAnalysisPanel';
import { MediaLibraryModal } from '../../components/MediaLibraryModal';
import { ResponsivePreviewPanel } from '../../components/ResponsivePreviewPanel';

export const BlogPostEditor: React.FC = () => {
  const { siteId, postId } = useParams<{ siteId: string; postId: string }>();
  const isMimicking = useIsMimicking();
  const isNewPost = postId === 'new';

  const state = useBlogPostEditorState();
  const handlers = useBlogPostEditorHandlers({
    siteId,
    postId,
    isNewPost,
    state,
  });

  const { blogPostStatusMap, refetch: refetchPublishStatus } = useUnpublishedSummary(siteId);
  const publishStatus = !isNewPost && postId ? blogPostStatusMap[postId] : undefined;

  const handleSaveWithRefetch = useCallback(
    async (publish: boolean) => {
      await handlers.handleSave(publish);
      await refetchPublishStatus();
    },
    [handlers, refetchPublishStatus]
  );

  const handleTogglePublishWithRefetch = useCallback(async () => {
    await handlers.handleTogglePublish();
    await refetchPublishStatus();
  }, [handlers, refetchPublishStatus]);

  // Load initial data
  useEffect(() => {
    handlers.loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, postId]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (state.hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [state.hasUnsavedChanges]);

  // Calculate word count and reading time
  const wordCount = state.content
    .replace(/<[^>]*>/g, '')
    .split(/\s+/)
    .filter(Boolean).length;
  const readingTime = Math.max(1, Math.ceil(wordCount / 200));

  // Blog post preview data — memoized to avoid unnecessary re-renders
  const previewData = useMemo(
    () =>
      state.showPreview
        ? {
            title: state.title,
            content: state.content,
            excerpt: state.excerpt,
            featuredImageUrl: state.featuredImageUrl,
            featuredImageCrop: state.featuredImageCrop,
            authorName: state.authorName,
            tags: state.tags,
            readingTimeMinutes: readingTime,
          }
        : undefined,
    [
      state.showPreview,
      state.title,
      state.content,
      state.excerpt,
      state.featuredImageUrl,
      state.featuredImageCrop,
      state.authorName,
      state.tags,
      readingTime,
    ]
  );

  const {
    html: previewHtml,
    isLoading: previewLoading,
    error: previewError,
    refresh: previewRefresh,
  } = useBlogPostPreview(siteId, previewData, state.showPreview);

  if (state.isLoading) {
    return (
      <div className="page-container flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="w-12 h-12 mx-auto mb-4 spinner"></div>
          <p className="text-gray-600 font-medium">Loading post...</p>
        </div>
      </div>
    );
  }

  if (!state.site) {
    return (
      <div className="page-container flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <h2 className="text-2xl font-bold text-gray-900">Site not found</h2>
          <p className="mt-2 text-gray-600">The site you're looking for doesn't exist.</p>
          <Link to="/dashboard" className="mt-4 btn-primary inline-block">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`page-container ${isMimicking ? 'mt-[52px]' : ''}`}>
      <BlogPostEditorHeader
        siteId={siteId!}
        isNewPost={isNewPost}
        post={state.post}
        site={state.site}
        blog={state.blog}
        title={state.title}
        hasUnsavedChanges={state.hasUnsavedChanges}
        isSaving={state.isSaving}
        isPublishing={state.isPublishing}
        wordCount={wordCount}
        readingTime={readingTime}
        publishStatus={publishStatus}
        onSave={handleSaveWithRefetch}
        onDelete={() => state.setShowDeleteModal(true)}
        onTogglePublish={handleTogglePublishWithRefetch}
        onShowSeoAnalysis={() => state.setShowSeoAnalysis(true)}
        onShowMediaLibrary={() => state.setShowMediaLibrary(true)}
        onShowPreview={() => state.setShowPreview(true)}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <BlogPostEditorContent
              title={state.title}
              content={state.content}
              excerpt={state.excerpt}
              onTitleChange={value => state.handleFieldChange(state.setTitle, value)}
              onContentChange={value => state.handleFieldChange(state.setContent, value)}
              onExcerptChange={value => state.handleFieldChange(state.setExcerpt, value)}
              onImageUpload={handlers.handleContentImageUpload}
              siteId={siteId}
            />

            <BlogPostEditorSidebar
              featuredImageUrl={state.featuredImageUrl}
              featuredImageCrop={state.featuredImageCrop}
              authorName={state.authorName}
              slug={state.slug}
              postStatus={state.post?.status}
              tags={state.tags}
              newTag={state.newTag}
              seoTitle={state.seoTitle}
              seoDescription={state.seoDescription}
              seoImageUrl={state.seoImageUrl}
              title={state.title}
              excerpt={state.excerpt}
              showSeoPanel={state.showSeoPanel}
              isUploadingImage={state.isUploadingImage}
              siteId={siteId}
              onFeaturedImageUpload={handlers.handleImageUpload}
              onFeaturedImageUrlChange={url => {
                state.setFeaturedImageUrl(url);
                state.setHasUnsavedChanges(true);
              }}
              onRemoveFeaturedImage={handlers.handleRemoveFeaturedImage}
              onFeaturedImageCropChange={crop => {
                state.setFeaturedImageCrop(crop);
                state.setHasUnsavedChanges(true);
              }}
              onAuthorNameChange={value => state.handleFieldChange(state.setAuthorName, value)}
              onSlugChange={value => state.handleFieldChange(state.setSlug, value)}
              onNewTagChange={state.setNewTag}
              onAddTag={handlers.handleAddTag}
              onRemoveTag={handlers.handleRemoveTag}
              onSeoTitleChange={value => state.handleFieldChange(state.setSeoTitle, value)}
              onSeoDescriptionChange={value =>
                state.handleFieldChange(state.setSeoDescription, value)
              }
              onSeoImageUrlChange={value => state.handleFieldChange(state.setSeoImageUrl, value)}
              onToggleSeoPanel={() => state.setShowSeoPanel(!state.showSeoPanel)}
            />
          </div>
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={state.showDeleteModal}
        onClose={() => state.setShowDeleteModal(false)}
        onConfirm={handlers.handleDelete}
        isLoading={state.isDeleting}
        title="Delete Post"
        message={`Are you sure you want to delete "${state.title}"? This action cannot be undone.`}
      />

      {/* SEO Analysis Panel */}
      {postId &&
        state.blog &&
        (() => {
          console.log('🔍 SEO Panel Props:', {
            postId,
            blogId: state.blog.id,
            isNewPost,
            postIdType: typeof postId,
            postIdLength: postId?.length,
          });
          return (
            <SEOAnalysisPanel
              blogId={state.blog.id}
              postId={postId!}
              isOpen={state.showSeoAnalysis}
              onClose={() => state.setShowSeoAnalysis(false)}
              onFixApplied={() => {
                void handlers.loadInitialData();
              }}
              title={`SEO Analysis - ${state.post?.title || 'Post'}`}
            />
          );
        })()}

      {/* Media Library Modal */}
      <MediaLibraryModal
        isOpen={state.showMediaLibrary}
        onClose={() => state.setShowMediaLibrary(false)}
        siteId={siteId}
      />

      {/* Blog Post Preview Panel */}
      <ResponsivePreviewPanel
        siteId={siteId || ''}
        pageId={postId || ''}
        isOpen={state.showPreview}
        onClose={() => state.setShowPreview(false)}
        externalPreview={{
          html: previewHtml,
          isLoading: previewLoading,
          error: previewError,
          refresh: previewRefresh,
        }}
      />

      {/* Toast Notification */}
      <ToastContainer toasts={state.toasts} removeToast={state.removeToast} />
    </div>
  );
};

export default BlogPostEditor;
